import { useQuery } from "@tanstack/react-query";
import ApiRequest from "../ApiRequest";
import { requestURLs } from "@/Environment";
import { QUERY_KEYS } from "./querykeys";

export interface DeliverableListItem {
  deliverableId: string;
  deliverableNameId: string;
  name: string;
  reference?: string | null;
  status: "NOT_STARTED" | "IN_PROGRESS" | "DELIVERED" | "APPROVED" | "REWORK_REQUIRED" | "CANCELLED";
  deliveryDueDate: string;
  approvalDueDate?: string | null;
  actualDeliveryDate?: string | null;
  actualApprovalDate?: string | null;
  approvalRequired: boolean;
  projectId?: string;
  project?: { projectId: string; projectName: string; projectCode?: string | null };
  destination?: { listValueId: string; value: string };
  type?: { listValueId: string; value: string };
  owner?: { userId: string; firstName: string; lastName: string; email: string };
  approver?: { userId: string; firstName: string; lastName: string; email: string };
  isDeliveryOverdue: boolean;
  deliveryOverdueDays: number;
  isApprovalOverdue: boolean;
  approvalOverdueDays: number;
  deliveryVarianceDays: number | null;
  approvalVarianceDays: number | null;
  isClosed: boolean;
  linkedTasksCount: number;
}

export interface DeliverablesResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  items: DeliverableListItem[];
}

export interface DeliverablesFilterParams {
  search?: string;
  destinationId?: string;
  destinationIds?: string[];
  typeId?: string;
  typeIds?: string[];
  ownerId?: string;
  ownerIds?: string[];
  assignedToUserId?: string;
  onlyMyDeliverables?: boolean;
  status?: string;
  statuses?: string[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export default function useDeliverablesQuery(projectId?: string, filters?: DeliverablesFilterParams) {
  return useQuery({
    queryKey: [QUERY_KEYS.deliverables, projectId, filters],
    queryFn: async () => {
      if (!projectId) return null;
      const params = new URLSearchParams();
      if (filters?.search) params.append("search", filters.search);
      if (filters?.onlyMyDeliverables) params.append("onlyMyDeliverables", "true");
      if (filters?.assignedToUserId) params.append("assignedToUserId", filters.assignedToUserId);
      if (filters?.destinationIds && filters.destinationIds.length > 0) {
        params.append("destinationIds", filters.destinationIds.join(","));
      } else if (filters?.destinationId) {
        params.append("destinationId", filters.destinationId);
      }
      if (filters?.typeIds && filters.typeIds.length > 0) {
        params.append("typeIds", filters.typeIds.join(","));
      } else if (filters?.typeId) {
        params.append("typeId", filters.typeId);
      }
      if (filters?.ownerIds && filters.ownerIds.length > 0) {
        params.append("ownerIds", filters.ownerIds.join(","));
      } else if (filters?.ownerId) {
        params.append("ownerId", filters.ownerId);
      }
      if (filters?.statuses && filters.statuses.length > 0) {
        params.append("statuses", filters.statuses.join(","));
      } else if (filters?.status) {
        params.append("status", filters.status);
      }
      if (filters?.page) params.append("page", String(filters.page));
      if (filters?.limit) params.append("limit", String(filters.limit));
      if (filters?.sortBy) params.append("sortBy", filters.sortBy);
      if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);

      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await ApiRequest.get<{ data: DeliverablesResponse }>(
        `${requestURLs.deliverables}/${projectId}${qs}`
      );
      return res.data;
    },
    enabled: Boolean(projectId),
  });
}
